import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CenteredContent, CenteredSpinner, Title } from '@/common/components';
import { useAuth, useGlobalTitle } from '@/hooks';
import usePageTracking from '@/hooks/usePageTracking';
import {
  createUpcomingViewModel,
  BookingSessionViewModel,
  isValidRoleFromStr,
  removeQueryString,
} from '@/common/utils';
import { FeedbackModal } from '@/common/components/feedback-modal';
import { useWaitingForFeedbackQuery } from '@/hooks/useWaitingForFeedbackQuery';
import { UserRole } from '@/constants';
import useApplicationSettings from '@/hooks/useApplicationSettings';
import { RebuyModal } from '@/common/components/rebuy-modal';
import { ReportSessionItem } from '../home/components/report-session-item';

export default function MyFeedBacks() {
  const { t } = useTranslation();
  const { appSettings } = useApplicationSettings();
  const [selectedSession, setSelectedSession] = useState<BookingSessionViewModel>();
  const [searchParams] = useSearchParams();
  const [dialogAction, setDialogAction] = useState<
    'view-session' | 'view-feedback' | 'view-rebuy'
  >();
  const { isAdvisor, isUser, selectedRole, setSelectedRole } = useAuth();

  const { data, isLoading } = useWaitingForFeedbackQuery({
    purpose: isAdvisor ? 'advisor-all-feedbacks' : 'user-feedbacks',
    onSuccess(data) {
      const bookingId = searchParams.get('bookingId');
      const role = searchParams.get('role') || ''; // for case user has both advisor and user role
      const isValidRole = isValidRoleFromStr(role);

      if (isValidRole && selectedRole !== role) {
        setSelectedRole(role as UserRole);
        removeQueryString();
        window.location.reload();
        return;
      }

      if (bookingId) {
        const selectedSession = (data?.items || []).find((s) => s.id?.toString() === bookingId);
        if (selectedSession) {
          setDialogAction('view-feedback');
          setSelectedSession(createUpcomingViewModel(selectedSession, true));
        }
      }
    },
  });

  const onCancel = useCallback(() => {
    setSelectedSession(undefined);
    setDialogAction(undefined);
  }, []);

  useGlobalTitle(t('myFeedbacks.title'));
  usePageTracking();

  if (isLoading) {
    return (
      <CenteredContent>
        <CenteredSpinner />
      </CenteredContent>
    );
  }

  if (!appSettings.isFeedbackFeatureEnabled && isAdvisor) {
    return undefined;
  }

  if (!appSettings.isUserFeedbackFeatureEnabled && isUser) {
    return undefined;
  }

  return (
    <CenteredContent size="large">
      <Title noMarginTop type="x-large">
        {isAdvisor
          ? t('myFeedbacks.advisorMyFeedbacksSubtitle')
          : t('myFeedbacks.userMyFeedbacksSubtitle')}
      </Title>
      {data?.items?.map((session) => {
        const viewModel = createUpcomingViewModel(session, isAdvisor);

        return (
          <ReportSessionItem
            key={session.id}
            item={viewModel}
            feedbackProps={{
              onFeedbackBtnClick: () => {
                setDialogAction('view-feedback');
                setSelectedSession(viewModel);
              },
              feedbackStatus: viewModel.feedbackStatus,
            }}
            isAdvisor={isAdvisor}
            onRebuyBtnClick={(s) => {
              setDialogAction('view-rebuy');
              setSelectedSession(s);
            }}
          />
        );
      })}
      {dialogAction === 'view-feedback' && selectedSession && (
        <FeedbackModal hideMarkAsReadBtn session={selectedSession} onCancel={onCancel} />
      )}

      {dialogAction === 'view-rebuy' && selectedSession && (
        <RebuyModal
          session={selectedSession}
          onCancel={() => {
            setSelectedSession(undefined);
            setDialogAction(undefined);
          }}
        />
      )}
    </CenteredContent>
  );
}
